import React, { useMemo, useState } from 'react';
import { Tabs, TabsHeader, TabsBody, Tab, TabPanel, Card, CardBody } from '@material-tailwind/react';
import { useNavigate } from 'react-router-dom';
// BsClockHistory is not used in your provided code, so I'm removing it. If you need it, you can add it back.
// import { BsClockHistory } from "react-icons/bs";
import AttentionSpanLineChart from './Charts/NewAttentionSpan';
import EngagementRateChart from './Charts/EngagementRateChart';
import useScroll from './useScroll';
import { apiBaseUrl } from '../../APIs/endpoints';
import { useParams } from 'react-router-dom';
import CustomDropdown from '../../commonComp/CustomDropdown';
import AttendanceIcon from '../../commonComp/AttendanceIcon.svg';
import EmotionalStateChart from './Charts/EmotionSatechart';

const ProgressBar = ({ now, className }) => (
    <div className={`w-full bg-gray-200 rounded-full h-2 ${className}`}>
        <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${now}%` }}></div>
    </div>
);

const StudentImageWithTooltip = ({ student, apiBaseUrl, isLast }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
        <div
            className="relative z-10"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <img
                src={`${apiBaseUrl}${student.student_photo}`}
                alt={student.student_name}
                className="w-6 h-6 rounded-full object-cover transition-transform duration-200 hover:scale-110 mt-2 mb-2"
            />
            {isHovered && (
                <div
                    className="absolute left-full ml-3 bg-gray-200 rounded-lg shadow-lg border border-gray-400 
                               w-16 h-16 flex items-center justify-center z-[75]"
                    style={{
                        top: "50%",
                        transform: "translateY(-50%)",
                        marginTop: isLast ? -6 : "0.75rem", 
                        marginBottom: isLast ? 2 : "auto",
                    }}
                >
                    <img
                        src={`${apiBaseUrl}${student.student_photo}`}
                        alt={student.student_name}
                        className="w-16 h-16 rounded-lg object-cover"
                    />
                </div>
            )}
        </div>
    );
};



const BehaviourRightSec = (props) => {
    const { data, metadata, onTabChange, activeTabIndex, fromDate, toDate, classInfo } = props;

    const [sortOrder, setSortOrder] = useState('bottom-to-top');
    const navigate = useNavigate();
    const { date: urlDate } = useParams();

    const sortedStudents = useMemo(() => {
        if (!data || activeTabIndex == null || !data[activeTabIndex]?.individual_participation) {
            return [];
        }

        const students = data[activeTabIndex].individual_participation;

        let sortedData = [...students].map(student => ({
            ...student,
            avg_attention_engage: parseFloat(student.avg_attention_engage) || 0,
        }));

        if (sortOrder === 'top-to-bottom') {
            return sortedData.sort((a, b) => b.avg_attention_engage - a.avg_attention_engage);
        } else {
            return sortedData.sort((a, b) => a.avg_attention_engage - b.avg_attention_engage);
        }
    }, [data, activeTabIndex, sortOrder]);

    const sortOptions = [
        { label: 'Bottom to Top', value: 'bottom-to-top' },
        { label: 'Top to Bottom', value: 'top-to-bottom' },
    ];

    const handleSortChange = (value) => {
        setSortOrder(value);
    };

    // This function is not used in the provided code, but I've updated it for the new structure.
    function gotoStudent() {
        if (!data || activeTabIndex == null) return;
        const activePeriod = data[activeTabIndex];

        const navigationData = {
            fromDate, toDate,
            grade: classInfo,
            subject: activePeriod.subject,
            // Assuming section comes from metadata as it's not in the period-specific data
            section: metadata?.sections?.[0] || 'A',
        };
        navigate("/student-behaviour", { state: navigationData });
    }

    const handleStudentClick = (studentId, studentName) => {
        localStorage.setItem('studentName', studentName);
        localStorage.setItem("studentId", studentId);

        const LOCAL_STORAGE_START_DATE_KEY = 'global_start_date';
        const LOCAL_STORAGE_END_DATE_KEY = 'global_end_date';

        if (urlDate) {
            const [day, month, year] = urlDate.split('-');
            const formattedDate = `${year}-${month}-${day}`;
            localStorage.setItem(LOCAL_STORAGE_START_DATE_KEY, formattedDate);
            localStorage.setItem(LOCAL_STORAGE_END_DATE_KEY, formattedDate);
        } else {
            localStorage.removeItem(LOCAL_STORAGE_START_DATE_KEY);
            localStorage.removeItem(LOCAL_STORAGE_END_DATE_KEY);
        }
        navigate(`/StudentPerformance/${studentId}/${studentName}`);
    };

    const StatCard = ({ title, value, total, Icon }) => (
        <Card className="border-none shadow-none bg-[#F4F8FF]">
            <CardBody className="flex justify-between items-center p-4">
                <div>
                    <p className="text-gray-900 text-base font-semibold font-[inter]">{title}</p>
                    <span className="text-2xl font-bold text-gray-800">{value}</span>
                    <span className="text-base font-bold text-gray-800">/{total}</span>
                </div>
                <div className="mr-4 rounded-full">
                    <img src={Icon} alt="Icon" className="h-10 w-10" />
                </div>
            </CardBody>
        </Card>
    );

    const TabContent = ({ periodData }) => {
        const attentionIntervals = periodData.attention_intervals || [];
        // const attentionAverage = periodData.avg_attention_period || 0;
        const values = attentionIntervals.map(interval =>
            typeof interval === "number" ? interval : interval.attention
        );
        const rawAverage = values.reduce((acc, val) => acc + (val || 0), 0) / (values.length || 1);
        const attentionAverage = Math.round(rawAverage * 100) / 100;


        const engagementIntervals = periodData.engage_intervals || [];
        // const engagementAverage = periodData.avg_engage_period || 0;
        const engagementValues = engagementIntervals.map(interval =>
            typeof interval === "number" ? interval : interval.engagement
        );
        const rawEngagementAverage = engagementValues.reduce((acc, val) => acc + (val || 0), 0) / (engagementValues.length || 1);
        const engagementAverage = Math.round(rawEngagementAverage * 100) / 100;

        const attendance = periodData.attendance || {};
        const attended = attendance.present || 0;
        const totalStudents = attendance.total_students || 0;

        const emotionalState = periodData.emotional_state || {};
        const isScrolling = useScroll();
        const scrollbarClass = `hidden-scrollbar ${isScrolling ? 'is-scrolling' : ''}`;

        function calculateDurationInMinutes(startTime, endTime) {
            if (!startTime || !endTime) return 0;
            const [startHours, startMinutes] = startTime.split(':').map(Number);
            const [endHours, endMinutes] = endTime.split(':').map(Number);
            const totalStartMinutes = (startHours * 60) + startMinutes;
            const totalEndMinutes = (endHours * 60) + endMinutes;
            return totalEndMinutes - totalStartMinutes;
        }

        const duration = calculateDurationInMinutes(periodData.start_time, periodData.end_time);

        return (
            <div className={`grid grid-cols-1 lg:grid-cols-5 gap-6`} style={{ margin: '5px' }}>
                {/* Left Section: Charts */}
                <div className="lg:col-span-3 space-y-6">
                    <Card className="shadow-none border">
                        <CardBody className="p-3 sm:p-4">
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mt-2 ml-[8px]">
                                <h6 className="font-bold text-[#000000] text-base sm:text-md lg:text-lg">
                                    Attention Span
                                </h6>
                                <div className="flex flex-wrap items-center text-xs sm:text-sm mt-[-7px]">
                                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1"></span>
                                    <span>Period Start:</span>
                                    <span className="ml-1 mr-3">{periodData.start_time}</span>
                                    <span className="w-1.5 h-1.5 bg-red-500 rounded-full mr-1"></span>
                                    <span>Period End:</span>
                                    <span className="ml-1">{periodData.end_time}</span>
                                </div>
                            </div>
                            <p className="flex flex-col text-gray-500 text-xs sm:text-sm leading-tight ml-[8px]">
                                <span className="text-2xl sm:text-3xl lg:text-4xl text-gray-900 font-bold">
                                    {attentionAverage}%
                                </span>
                                Average Attention
                            </p>
                            <div className="relative w-full min-h-[180px] sm:min-h-[200px] lg:min-h-[240px] ml-[8px]">
                                <AttentionSpanLineChart
                                    chartData={attentionIntervals}
                                    attentionIntervalDuration={periodData.attention_interval_duration}
                                    duration={duration}
                                />
                            </div>
                        </CardBody>
                    </Card>

                    <Card className="shadow-none border pt-2" >
                        <CardBody className="p-3 sm:p-4">
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center ml-3">
                                <h6 className="font-bold text-[#000000] text-base sm:text-md lg:text-lg">
                                    Engagement Rate
                                </h6>
                            </div>
                            <p className="flex flex-col text-gray-500 text-xs sm:text-sm leading-tight ml-3 ">
                                <span className="text-2xl sm:text-3xl lg:text-4xl text-gray-900 font-bold">
                                    {engagementAverage}%
                                </span>
                                Average Engagement
                            </p>
                            <div className="relative w-full min-h-[180px] sm:min-h-[200px] lg:min-h-[240px] mt-[-20px]">
                                <EngagementRateChart
                                    engagementData={engagementIntervals}
                                    engage_interval_duration={periodData.engage_interval_duration}
                                    duration={duration}
                                />
                            </div>
                        </CardBody>
                    </Card>

                    <Card className="shadow-none border">
                        <CardBody>
                            <h6 className="font-bold text-[#000000] text-base sm:text-md lg:text-lg ml-[8px]">Emotional State</h6>
                            <div className="relative w-full min-h-[180px] sm:min-h-[200px] lg:min-h-[240px] mt-[-36px] ml-[8px]">
                                <div className="mt-5">
                                    <EmotionalStateChart emotionData={emotionalState} />
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </div>

                {/* Right Section: Stats and Leaderboard */}
                <div className="lg:col-span-2 flex flex-col gap-6">
                    <StatCard title="Attendance" value={attended} total={totalStudents} Icon={AttendanceIcon} />
                    <Card className="shadow-none border flex-grow overflow-hidden h-[40rem]">
                        <CardBody className="flex flex-col h-full relative p-4 overflow-visible">
                            <div className="mb-4">
                                <h6 className="font-bold text-gray-800 text-lg mb-2">
                                    Student Behaviour Leaderboard
                                </h6>
                                <div className="w-auto">
                                    <CustomDropdown
                                        options={sortOptions}
                                        value={sortOrder}
                                        onChange={handleSortChange}
                                        placeholder="Sort Order"
                                        buttonClass="w-fit text-md font-[Archivo]"
                                        menuClass="w-full"
                                    />
                                </div>
                            </div>
                            <div className={`pr-3 overflow-y-auto flex-grow ${scrollbarClass} scrollbar-hide`}>
                                <div className="flex flex-col gap-4">
                                    {sortedStudents.map((student, idx) => {
                                        const isLast = idx === sortedStudents.length - 1;

                                        return (
                                            <div
                                                key={student.student_id}
                                                className="flex items-center w-full space-x-3 cursor-pointer"
                                                onClick={() => handleStudentClick(student.student_id, student.student_name)}
                                            >
                                                <div className="flex-grow">
                                                    <div className="flex justify-between items-center">
                                                        <div className="flex items-center">
                                                            <StudentImageWithTooltip
                                                                student={student}
                                                                apiBaseUrl={apiBaseUrl}
                                                                isLast={isLast} // Pass the flag
                                                            />
                                                            <span className="text-sm font-medium text-gray-700 ml-2">{student.student_name}</span>
                                                        </div>
                                                        <span className="text-sm font-bold text-gray-800">{student.avg_attention_engage}%</span>
                                                    </div>
                                                    <ProgressBar now={student.avg_attention_engage} />
                                                </div>
                                            </div>
                                        );
                                    })}

                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </div>
            </div>
        );
    };

    const periods = Array.isArray(data) ? data : [];

    if (activeTabIndex == null || periods.length === 0) {
        return <div className="p-4 text-center text-gray-500">No data available for the selected date.</div>;
    }

    return (
        <div className="bg-white pt-2" style={{ margin: '5px' }}>
            <Tabs value={activeTabIndex}>
                <TabsHeader
                    className='rounded-none border-b border-t border-blue-gray-50 bg-transparent gap-4 overflow-x-auto whitespace-nowrap'
                    indicatorProps={{ className: 'bg-transparent border-0 shadow-none rounded-none' }}
                >
                    {periods.map((period, index) => (
                        <Tab
                            key={index} // Use index as key
                            value={index} // Use index as value
                            onClick={() => onTabChange(index)}
                            className={`w-auto px-1 ${activeTabIndex === index ? 'text-blue-500 ml-2 font-semibold' : 'text-[#14151A]'}`}
                        >
                            <div className="flex flex-col items-center">
                                <span>{period.subject.charAt(0).toUpperCase() + period.subject.slice(1)}</span>
                                <div className={`h-0.5 w-4 rounded-full mt-1 ${activeTabIndex === index ? 'bg-blue-500' : 'bg-transparent'}`}></div>
                            </div>
                        </Tab>
                    ))}
                </TabsHeader>
                <TabsBody>
                    {periods.map((period, index) => (
                        <TabPanel key={index} value={index} className="p-2 md:p-4">
                            {activeTabIndex === index && <TabContent periodData={period} />}
                        </TabPanel>
                    ))}
                </TabsBody>
            </Tabs>
        </div>
    );
};

export default BehaviourRightSec;